import { Tolgee, VueTolgee, FormatSimple, LanguageStorage } from '@tolgee/vue';
import { LocalStorage } from 'quasar';
import logger from 'src/utils/logger';
import { safeBoot } from 'src/utils/bootSafety';

// Inlined to avoid importing src/constants here: that module evaluates
// i18n.t(...) at the top level, which isn't ready this early in boot (and
// breaks unit tests that import this file). These mirror src/constants.ts:
//   selected_language, DEFAULT_LANGUAGE, preferred_language_setting.
const SELECTED_LANGUAGE_KEY = 'selected_language';
const DEFAULT_LANGUAGE = 'en-us';
const PREFERRED_LANGUAGE_SETTING = 'PREFERRED_LANGUAGE';

// Available languages that have translation files
const AVAILABLE_LANGUAGES = [
  'en', 'de', 'it', 'fr', 'es', 'nl', 'zh',
  'bg', 'cs', 'hu', 'ja', 'ko', 'pl', 'pt', 'pt-br',
  'ro', 'sk', 'sl', 'sv', 'th', 'tr', 'vi'
];

/**
 * Normalize and validate language code
 * Handles variants like 'en-us' → 'en', 'pt-BR' → 'pt-br'
 * Falls back to 'en' if language is not available
 *
 * @param {string} lang - Language code from localStorage
 * @returns {string} - Normalized language code that exists in AVAILABLE_LANGUAGES
 */
function normalizeLanguage(lang) {
  if (!lang || typeof lang !== 'string') {
    return 'en';
  }

  // Normalize to lowercase
  const normalized = lang.toLowerCase().trim();

  // If exact match exists, use it
  if (AVAILABLE_LANGUAGES.includes(normalized)) {
    return normalized;
  }

  // Handle regional variants (e.g., 'en-us' → 'en', 'pt-br' stays as 'pt-br')
  // First check if the full regional code exists
  const withHyphen = normalized.replace('_', '-');
  if (AVAILABLE_LANGUAGES.includes(withHyphen)) {
    return withHyphen;
  }

  // Extract base language (before hyphen or underscore)
  const baseLanguage = normalized.split(/[-_]/)[0];
  if (AVAILABLE_LANGUAGES.includes(baseLanguage)) {
    logger.tolgee(`Language '${lang}' normalized to '${baseLanguage}'`);
    return baseLanguage;
  }

  // Fallback to English
  logger.tolgee(`Language '${lang}' not available, falling back to 'en'`);
  return 'en';
}

// Get user's language preference from our app's LocalStorage BEFORE initializing Tolgee.
// Two storage keys to consider:
//   - `selected_language` — our app's explicit user choice (set by the
//     language picker).
//   - `__tolgee_currentLanguage` — written by Tolgee's LanguageStorage
//     plugin. On first visit it picks `navigator.language` verbatim
//     (e.g. `en-us`, `pt-br`), which often doesn't match our
//     AVAILABLE_LANGUAGES list and silently renders raw keys before
//     the fallback kicks in. Normalise both and overwrite the Tolgee
//     slot up-front so the plugin reads a clean value.
const explicitSelected = LocalStorage.getItem('selected_language');
const rawLanguage =
  explicitSelected ||
  (typeof localStorage !== 'undefined'
    ? localStorage.getItem('__tolgee_currentLanguage')
    : null) ||
  (typeof navigator !== 'undefined' ? navigator.language : null) ||
  'en';
const userLanguage = normalizeLanguage(rawLanguage);

// Persist the regional setting code (e.g. 'en-us') to `selected_language` when
// the user has not made an explicit choice yet. The app's setting/dropdown/backend
// all use regional codes, so storing the normalised base ('en') here would leave
// the language picker unable to highlight the active option. English is the only
// base→regional case we can infer pre-login (en → DEFAULT_LANGUAGE 'en-us'); other
// languages keep their resolved code and, once logged in, the backend pref (which
// may carry a region such as 'es-es') takes precedence.
if (!explicitSelected) {
  const settingCode = userLanguage === 'en' ? DEFAULT_LANGUAGE : userLanguage;
  LocalStorage.set(SELECTED_LANGUAGE_KEY, settingCode);
}
// Sanity-overwrite: if Tolgee's own slot holds a non-available value
// (the classic `en-us` / `en-gb` case), replace it with the normalised
// equivalent so subsequent Tolgee reads never see an invalid code.
try {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('__tolgee_currentLanguage');
    if (stored && !AVAILABLE_LANGUAGES.includes(stored.toLowerCase())) {
      localStorage.setItem('__tolgee_currentLanguage', userLanguage);
    }
  }
} catch {
  // LocalStorage access can throw on strict privacy settings — non-fatal.
}
logger.tolgee(
  'User language preference (selected_language / __tolgee_currentLanguage):',
  rawLanguage,
  '→ normalized to:',
  userLanguage,
);

// Loader map shared by both `staticData` (Tolgee's lazy contract) and
// the explicit pre-load step in safeBoot below. Defining the imports
// here once means the Vite static-analyser still picks them all up
// (so each locale lands in its own code-split chunk) AND we can
// invoke them eagerly when needed.
//
// Bug-2026-05-04: on dev (post `restore production app mount` deploy)
// Tolgee's lazy staticData path entered an inconsistent state — the
// import promise for `en` resolved, `isLoaded()` flipped to true, but
// `getRecord('en')` stayed empty and every `t()` call returned the
// raw key. Live forensics confirmed: feeding the same JSON via
// `addStaticData()` after init populated the record correctly. The
// safeBoot wrapper below now forces that path on every boot — single
// source of truth for the user's language plus the `en` fallback.
const STATIC_DATA_LOADERS = {
  en: () => import('src/tolgee/i18n/en.json').then(m => m.default || m),
  de: () => import('src/tolgee/i18n/de.json').then(m => m.default || m),
  it: () => import('src/tolgee/i18n/it.json').then(m => m.default || m),
  fr: () => import('src/tolgee/i18n/fr.json').then(m => m.default || m),
  es: () => import('src/tolgee/i18n/es.json').then(m => m.default || m),
  nl: () => import('src/tolgee/i18n/nl.json').then(m => m.default || m),
  zh: () => import('src/tolgee/i18n/zh.json').then(m => m.default || m),
  bg: () => import('src/tolgee/i18n/bg.json').then(m => m.default || m),
  cs: () => import('src/tolgee/i18n/cs.json').then(m => m.default || m),
  hu: () => import('src/tolgee/i18n/hu-HU.json').then(m => m.default || m),
  ja: () => import('src/tolgee/i18n/ja.json').then(m => m.default || m),
  ko: () => import('src/tolgee/i18n/ko-KR.json').then(m => m.default || m),
  pl: () => import('src/tolgee/i18n/pl.json').then(m => m.default || m),
  pt: () => import('src/tolgee/i18n/pt.json').then(m => m.default || m),
  'pt-br': () => import('src/tolgee/i18n/pt-BR.json').then(m => m.default || m),
  ro: () => import('src/tolgee/i18n/ro.json').then(m => m.default || m),
  sk: () => import('src/tolgee/i18n/sk.json').then(m => m.default || m),
  sl: () => import('src/tolgee/i18n/sl.json').then(m => m.default || m),
  sv: () => import('src/tolgee/i18n/sv.json').then(m => m.default || m),
  th: () => import('src/tolgee/i18n/th.json').then(m => m.default || m),
  tr: () => import('src/tolgee/i18n/tr.json').then(m => m.default || m),
  vi: () => import('src/tolgee/i18n/vi.json').then(m => m.default || m),
};

// ⭐ FIX: Initialize Tolgee with proper async loading to prevent missing key warnings
// Use staticData with optional missingKeyHandler to suppress warnings in build/test environments
const tolgee = Tolgee()
  .use(FormatSimple())
  .use(LanguageStorage())
  .init({
    availableLanguages: AVAILABLE_LANGUAGES,
    defaultLanguage: userLanguage, // Use user's language as default
    fallbackLanguage: 'en',
    staticData: STATIC_DATA_LOADERS,
    // Suppress missing key warnings during build/test - translation exists but loading is async
    // In dev/prod with proper async loading, this handler won't be triggered
    // FIX: Use import.meta.env.MODE instead of process.env.NODE_ENV (works in all build modes)
    missingKeyHandler: import.meta.env.MODE === 'test'
      ? 'silent'
      : import.meta.env.DEV
        ? (key) => { console.warn(`[i18n] Missing translation key: "${key.key}"`); }
        : undefined,
  });

// Eagerly load + register a single locale via addStaticData. Safe to
// call multiple times (Tolgee deduplicates on language+namespace).
async function _eagerLoad(lang) {
  const loader = STATIC_DATA_LOADERS[lang];
  if (!loader) return false;
  try {
    const data = await loader();
    if (data && typeof data === 'object') {
      tolgee.addStaticData({ [lang]: data });
      return true;
    }
  } catch (e) {
    logger.tolgee(`Eager staticData load failed for ${lang}:`, e);
  }
  return false;
}

export default safeBoot('tolgee', async ({ app }) => {
  // Bug-2026-05-04 fix: explicitly preload the user's language plus
  // the `en` fallback BEFORE `app.use(VueTolgee, ...)` so the first
  // render has populated records. Without this the staticData lazy
  // callback can hand Tolgee the JSON but the record never gets
  // committed (observed live: `isLoaded()=true` + `getRecord('en')`
  // returning empty + every `t()` falling back to the raw key).
  await _eagerLoad('en');
  if (userLanguage !== 'en') {
    await _eagerLoad(userLanguage);
  }

  app.use(VueTolgee, { tolgee });

  // Use tolgee.t() directly instead of useTranslate()
  app.config.globalProperties.tt = (key, params) => tolgee.t(key, params);

  // Belt-and-suspenders: force the language to the normalised value
  // right after init. If LanguageStorage previously wrote `en-us` (or
  // similar regional variant) before we added the sanity-overwrite
  // above, this ensures the live runtime uses the clean code even on
  // existing sessions. Safe on fresh sessions too.
  try {
    const current = tolgee.getLanguage?.();
    if (current !== userLanguage) {
      await tolgee.changeLanguage(userLanguage).catch(() => {});
    }
  } catch {
    // If the runtime doesn't support changeLanguage in this phase,
    // fallbackLanguage: 'en' still covers correctness.
  }

  // Log successful Tolgee initialization
  logger.tolgee('Tolgee initialized with language:', userLanguage);

  // Returning-user restore: if an auth token is already present at boot
  // (refresh / re-open while logged in), apply the user's backend
  // PREFERRED_LANGUAGE. Fire-and-forget on purpose — must not delay first
  // paint, and the settings store is dynamically imported so it stays out of
  // the boot/entry chunk (see CLAUDE.md "Build Optimization"). Fresh logins,
  // where the token arrives AFTER boot, are covered by the post-login reload.
  void restoreBackendLanguage();
});

async function restoreBackendLanguage() {
  try {
    const tokenDetails = LocalStorage.getItem('token_details');
    if (!tokenDetails || !tokenDetails.access_token) return; // not logged in at boot

    const { useSettingStore } = await import('src/store/Settings/setting.store');
    const settingsStore = useSettingStore();
    await settingsStore.startSettingsFetch();
    const backendLanguage = await settingsStore.getUserBasedSettings(PREFERRED_LANGUAGE_SETTING);
    if (!backendLanguage) return; // no server preference saved

    // Store the regional code verbatim; feed Tolgee the normalised base.
    LocalStorage.set(SELECTED_LANGUAGE_KEY, backendLanguage);
    const base = String(backendLanguage).split('-')[0];
    if (tolgee.getLanguage?.() !== base) {
      await tolgee.changeLanguage(base);
    }
    logger.tolgee('Restored backend language:', backendLanguage, '-> Tolgee:', base);
  } catch (e) {
    // Non-fatal: boot already applied a sensible default language.
    logger.tolgee('Backend language restore skipped:', e);
  }
}

export const tol = tolgee;

/**
 * Map a Tolgee base language code to the regional code the app setting and
 * language picker use. Tolgee reports 'en'; the dropdown option / DEFAULT_LANGUAGE
 * is 'en-us', so the active option only highlights when we map it back.
 * Only English needs this today; all other codes are returned unchanged.
 *
 * @param {string|null|undefined} code - language code from tolgee.getLanguage()
 * @returns {string} the regional code ('en' → 'en-us'), or DEFAULT_LANGUAGE if empty
 */
export function toRegionalLanguageCode(code) {
  if (!code) return DEFAULT_LANGUAGE;
  return code === 'en' ? DEFAULT_LANGUAGE : code;
}

// Export for testing
export { normalizeLanguage, AVAILABLE_LANGUAGES };
